import { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import Pagination from '../Pagination';
import api from '../../services/Api';
import Title from '../Title';

const SubscriptionPlan = () => {
  const [plans, setPlans] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [selectedPlanDetails, setSelectedPlanDetails] = useState(null);
  const [modalMode, setModalMode] = useState('add');
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    planCode: '',
    description: '',
    price: 0,
    currency: 'USD',
    badgeType: '',
    interval: '30 days',
    isActive: true,
    features: [],
  });
  const [featureInput, setFeatureInput] = useState('');

  const { currentPage, entriesPerPage } = useSelector(state => state.pagination);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await api.get('/admin/plans');
        if (Array.isArray(response.data.plans)) {
          setPlans(response.data.plans);
        } else {
          console.error('Expected an array but got:', response.data);
          setPlans([]);
        }
      } catch (error) {
        console.error('Error fetching plans:', error);
        setPlans([]);
      }
    };

    fetchData();
  }, []);

  const truncateText = (text, maxLength = 30) => {
    if (!text) return '';
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  const truncateFeatures = (features, maxItems = 2) => {
    if (!features || features.length === 0) return '-';
    if (features.length <= maxItems) {
      return features.map((feature, index) => (
        <div key={index} className="text-xs text-gray-600 truncate">
          • {truncateText(feature, 25)}
        </div>
      ));
    }
    return (
      <div>
        {features.slice(0, maxItems).map((feature, index) => (
          <div key={index} className="text-xs text-gray-600 truncate">
            • {truncateText(feature, 25)}
          </div>
        ))}
        <div className="text-xs text-blue-500 cursor-pointer hover:text-blue-700">
          +{features.length - maxItems} more features
        </div>
      </div>
    );
  };

  // const formatFeaturesDisplay = features => {
  //   if (!features || features.length === 0) return '-';

  //   return (
  //     <ul className="pl-4 space-y-1 list-disc">
  //       {features.map((feature, index) => (
  //         <li key={index} className="text-xs text-gray-600">
  //           {feature}
  //         </li>
  //       ))}
  //     </ul>
  //   );
  // };

  const handleInputChange = e => {
    const { name, value, type, checked } = e.target;

    if (name === 'price') {
      const numericValue = value.replace(/[^\d.]/g, '');
      const sanitizedValue = numericValue.replace(/(\..*)\./g, '$1');

      setFormData(prev => ({
        ...prev,
        [name]: sanitizedValue,
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value,
      }));
    }
  };

  const handleFeatureInputChange = e => {
    setFeatureInput(e.target.value);
  };

  const addFeature = () => {
    if (featureInput.trim() === '') return;

    setFormData(prev => ({
      ...prev,
      features: [...prev.features, featureInput.trim()],
    }));
    setFeatureInput('');
  };

  const removeFeature = index => {
    setFormData(prev => ({
      ...prev,
      features: prev.features.filter((_, i) => i !== index),
    }));
  };

  const handleKeyPress = e => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addFeature();
    }
  };

  const openAddModal = () => {
    setModalMode('add');
    setFormData({
      name: '',
      planCode: '',
      description: '',
      price: 0,
      currency: 'USD',
      badgeType: '',
      interval: '30 days',
      isActive: true,
      features: [],
    });
    setFeatureInput('');
    setIsModalOpen(true);
  };

  const openUpdateModal = (plan, e) => {
    e.stopPropagation();
    setModalMode('update');
    setSelectedPlan(plan);
    setFormData({
      name: plan.name,
      planCode: plan.planCode,
      description: plan.description,
      price: plan.price,
      currency: plan.currency,
      badgeType: plan.badgeType,
      interval: plan.interval,
      isActive: plan.isActive,
      features: Array.isArray(plan.features) ? plan.features : [],
    });
    setFeatureInput('');
    setIsModalOpen(true);
  };

  const openDetailModal = plan => {
    setSelectedPlanDetails(plan);
    setIsDetailModalOpen(true);
  };

  const handleSubmit = async e => {
    e.preventDefault();
    try {
      const submitData = {
        name: formData.name,
        planCode: formData.planCode,
        description: formData.description,
        price: parseFloat(formData.price) || 0,
        currency: formData.currency,
        badgeType: formData.badgeType,
        interval: formData.interval,
        isActive: formData.isActive,
        features: formData.features,
      };

      if (modalMode === 'add') {
        const response = await api.post('/admin/plans', submitData);
        setPlans(prevPlans => [...prevPlans, response.data]);
      } else {
        const response = await api.patch(`/admin/plans/${selectedPlan._id}`, submitData);
        setPlans(prevPlans =>
          prevPlans.map(plan =>
            plan._id === selectedPlan._id ? { ...plan, ...response.data } : plan
          )
        );
      }
      setIsModalOpen(false);
    } catch (error) {
      console.error(`Error ${modalMode === 'add' ? 'adding' : 'updating'} plan:`, error);
    }
  };

  const handleDeletePlan = async (planId, e) => {
    e.stopPropagation();
    if (window.confirm('Are you sure you want to delete this plan?')) {
      try {
        await api.delete(`/admin/plans/${planId}`);
        setPlans(prevPlans => prevPlans.filter(plan => plan._id !== planId));
      } catch (error) {
        console.error('Error deleting plan:', error);
      }
    }
  };

  const totalEntries = plans.length;
  const paginatedPlans = plans.slice(
    (currentPage - 1) * entriesPerPage,
    currentPage * entriesPerPage
  );

  const EmptyPlansState = () => (
    <div className="flex flex-col items-center justify-center py-16 text-center bg-white rounded-lg">
      <div className="mb-6 text-gray-400">
        <svg className="w-24 h-24 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={1}
            d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
      </div>
      <h3 className="mb-3 text-lg font-light text-gray-600">No subscription plans yet</h3>
      <p className="max-w-md mb-6 text-sm font-light text-gray-500">
        Get started by creating your first subscription plan. Plans help you manage different
        pricing tiers and features for your users.
      </p>
      <button
        onClick={openAddModal}
        className="px-6 py-2 text-sm font-light text-white transition-colors bg-black rounded hover:bg-gray-800">
        Create Your First Plan
      </button>
    </div>
  );

  return (
    <div className="p-4 sm:ml-64 w-[80%] h-screen bg-[#F4F5F6] font-nohemi">
      <div>
        <div className="mb-6">
          <Title title="Manage Subscription Plans" subtitle="Settings > Subscription > Plans" />
        </div>

        <div className="bg-white rounded-lg">
          <div className="flex items-center justify-between p-4">
            <h2 className="text-xs font-normal text-[#191B1C]">Plan List</h2>
            <div className="flex items-end justify-end gap-2">
              <button
                className="p-2 text-xs font-normal text-white transition-colors bg-black rounded focus:0 focus:outline-none hover:bg-gray-800"
                onClick={openAddModal}>
                Add Plan
              </button>
            </div>
          </div>

          {plans.length === 0 ? (
            <EmptyPlansState />
          ) : (
            <>
              <div className="relative overflow-x-auto">
                <table className="w-full mx-auto text-left">
                  <thead>
                    <tr className="text-sm font-medium text-[#808587] bg-[#F5F6F7]">
                      <th className="p-3 font-light">No</th>
                      <th className="p-3 font-light">Plan</th>
                      <th className="p-3 font-light">Plan Code</th>
                      <th className="p-3 font-light">Price</th>
                      <th className="p-3 font-light">Currency</th>
                      <th className="p-3 font-light text-nowrap">Created At</th>
                      <th className="p-3 font-light">Description</th>
                      <th className="p-3 font-light">Features</th>
                      <th className="p-3 font-light">Badge Type</th>
                      <th className="p-3 font-light">Duration</th>
                      <th className="p-3 font-light">Active</th>
                      <th className="p-3 font-light">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedPlans.map((plan, index) => (
                      <tr
                        key={plan?._id}
                        className="border-b font-light text-sm text-[#4A5154] hover:bg-[#F0F6FF] cursor-pointer"
                        onClick={() => openDetailModal(plan)}>
                        <td className="px-6 py-2">{index + 1}</td>
                        <td className="p-2 text-[#191B1C] font-medium">{plan.name}</td>
                        <td className="p-2">{plan.planCode}</td>
                        <td className="p-2">{plan.price}</td>
                        <td className="p-2">{plan.currency}</td>
                        <td className="p-2 whitespace-nowrap">
                          {new Date(plan.createdAt).toLocaleDateString()}
                        </td>
                        <td className="p-2 max-w-[150px]">
                          <div className="truncate" title={plan.description}>
                            {truncateText(plan.description, 40)}
                          </div>
                        </td>
                        <td className="p-2 max-w-[200px]">
                          <div onClick={e => e.stopPropagation()}>
                            {truncateFeatures(plan.features || [], 2)}
                          </div>
                        </td>
                        <td className="p-2">
                          <span
                            className={`inline-flex items-center px-2 py-1 text-xs rounded-full ${
                              plan.badgeType === 'goldTick'
                                ? 'bg-yellow-100 text-yellow-800'
                                : plan.badgeType === 'silverTick'
                                ? 'bg-gray-100 text-gray-800'
                                : 'bg-blue-100 text-blue-800'
                            }`}>
                            {plan.badgeType}
                          </span>
                        </td>
                        <td className="p-2 whitespace-nowrap">{plan.interval}</td>
                        <td className="p-2">
                          <span
                            className={`inline-flex items-center px-2 py-1 text-xs rounded-full ${
                              plan.isActive
                                ? 'bg-green-100 text-green-800'
                                : 'bg-red-100 text-red-800'
                            }`}>
                            {plan.isActive ? 'Yes' : 'No'}
                          </span>
                        </td>
                        <td className="flex p-2 space-x-1" onClick={e => e.stopPropagation()}>
                          <button
                            className="px-2 py-1 text-black transition-colors border border-black rounded focus:0 focus:outline-none hover:bg-gray-50"
                            onClick={e => openUpdateModal(plan, e)}>
                            Update
                          </button>
                          <button
                            className="px-2 py-1 text-white transition-colors bg-black rounded focus:0 focus:outline-none hover:bg-gray-800"
                            onClick={e => handleDeletePlan(plan._id, e)}>
                            Delete
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {plans.length > entriesPerPage && <Pagination totalEntries={totalEntries} />}
            </>
          )}
        </div>
      </div>

      {/* Add/Edit Plan Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white p-8 rounded-lg w-[850px] space-y-4 max-h-[90vh] overflow-y-auto">
            <h2 className="mb-4 text-xl font-semibold">
              {modalMode === 'add' ? 'Add New Plan' : 'Update Plan'}
            </h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-12">
                <div>
                  <label className="block mb-2 text-sm">Plan Name</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    className="w-full p-2 border border-gray-300 rounded bg-[#F5F6FA]"
                    placeholder="Enter plan name"
                    required
                  />
                </div>

                <div>
                  <label className="block mb-2 text-sm">Plan Code</label>
                  <input
                    type="text"
                    name="planCode"
                    value={formData.planCode}
                    onChange={handleInputChange}
                    className="w-full p-2 border border-gray-300 rounded bg-[#F5F6FA]"
                    placeholder="Enter plan code"
                    required
                  />
                </div>

                <div className="col-span-2">
                  <label className="block mb-2 text-sm">Description</label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    className="w-full p-2 border border-gray-300 rounded bg-[#F5F6FA] h-24"
                    placeholder="Enter plan description"
                    required
                  />
                </div>

                <div className="col-span-2">
                  <label className="block mb-2 text-sm">Features</label>
                  <div className="space-y-2">
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={featureInput}
                        onChange={handleFeatureInputChange}
                        onKeyPress={handleKeyPress}
                        className="flex-1 p-2 border border-gray-300 rounded bg-[#F5F6FA]"
                        placeholder="Enter a feature"
                      />
                      <button
                        type="button"
                        onClick={addFeature}
                        className="px-4 py-2 text-white bg-black rounded hover:bg-gray-800">
                        Add
                      </button>
                    </div>

                    {/* Display added features */}
                    {formData.features.length > 0 && (
                      <div className="p-3 mt-3 border border-gray-200 rounded bg-gray-50">
                        <h4 className="mb-2 text-sm font-medium">Added Features:</h4>
                        <ul className="space-y-1">
                          {formData.features.map((feature, index) => (
                            <li
                              key={index}
                              className="flex items-center justify-between p-2 bg-white rounded">
                              <span className="text-sm">{feature}</span>
                              <button
                                type="button"
                                onClick={() => removeFeature(index)}
                                className="text-red-500 hover:text-red-700">
                                Remove
                              </button>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    <p className="mt-1 text-xs text-gray-500">
                      Add features one by one. They will be displayed as bullet points in the table.
                    </p>
                  </div>
                </div>

                <div>
                  <label className="block mb-2 text-sm">Currency</label>
                  <select
                    name="currency"
                    value={formData.currency}
                    onChange={handleInputChange}
                    className="w-full p-2 border border-gray-300 rounded bg-[#F5F6FA]"
                    required>
                    <option value="USD">USD</option>
                    <option value="EUR">EUR</option>
                    <option value="GBP">GBP</option>
                    <option value="NGN">NGN</option>
                  </select>
                </div>

                <div>
                  <label className="block mb-2 text-sm">Duration</label>
                  <select
                    name="interval"
                    value={formData.interval}
                    onChange={handleInputChange}
                    className="w-full p-2 border border-gray-300 rounded bg-[#F5F6FA]"
                    required>
                    <option value="1 days">Daily (1 day)</option>
                    <option value="30 days">Monthly (30 days)</option>
                    <option value="7 days">Weekly (7 days)</option>
                    <option value="365 days">Yearly (365 days)</option>
                  </select>
                </div>
                <div>
                  <label className="block mb-2 text-sm">Price</label>
                  <input
                    type="text"
                    name="price"
                    value={formData.price}
                    onChange={handleInputChange}
                    className="w-full p-2 border border-gray-300 rounded bg-[#F5F6FA]"
                    placeholder="Enter price"
                    required
                  />
                </div>
                <div>
                  <label className="block mb-2 text-sm">Badge Type</label>
                  <select
                    name="badgeType"
                    value={formData.badgeType}
                    onChange={handleInputChange}
                    className="w-full p-2 border border-gray-300 rounded bg-[#F5F6FA]"
                    required>
                    <option value="blueTick">Blue Tick</option>
                    <option value="silverTick">Silver Tick</option>
                    <option value="goldTick">Gold Tick</option>
                  </select>
                </div>
              </div>
              <div className="flex items-center">
                <input
                  type="checkbox"
                  name="isActive"
                  checked={formData.isActive}
                  onChange={handleInputChange}
                  className="mr-2 accent-black"
                  id="isActiveCheckbox"
                />
                <label htmlFor="isActiveCheckbox" className="block text-sm">
                  Active Plan
                </label>
              </div>

              <div className="flex justify-end pt-4 space-x-2">
                <button
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  className="px-4 py-2 text-black transition-colors border border-black rounded hover:bg-gray-50">
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 text-white transition-colors bg-black rounded hover:bg-gray-800">
                  {modalMode === 'add' ? 'Submit' : 'Update'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Plan Details Modal */}
      {isDetailModalOpen && selectedPlanDetails && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white p-8 rounded-lg w-[800px] max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold">Plan Details</h2>
              <button
                onClick={() => setIsDetailModalOpen(false)}
                className="text-gray-500 hover:text-gray-700">
                ✕
              </button>
            </div>

            <div className="space-y-6">
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <h3 className="mb-2 text-sm font-medium text-gray-500">Plan Name</h3>
                  <p className="text-sm">{selectedPlanDetails.name}</p>
                </div>

                <div>
                  <h3 className="mb-2 text-sm font-medium text-gray-500">Plan Code</h3>
                  <p className="text-sm">{selectedPlanDetails.planCode}</p>
                </div>

                <div>
                  <h3 className="mb-2 text-sm font-medium text-gray-500">Price</h3>
                  <p className="text-sm">
                    {selectedPlanDetails.price} {selectedPlanDetails.currency}
                  </p>
                </div>

                <div>
                  <h3 className="mb-2 text-sm font-medium text-gray-500">Duration</h3>
                  <p className="text-sm">{selectedPlanDetails.interval}</p>
                </div>

                <div>
                  <h3 className="mb-2 text-sm font-medium text-gray-500">Badge Type</h3>
                  <span
                    className={`inline-flex items-center px-3 py-1 text-sm rounded-full ${
                      selectedPlanDetails.badgeType === 'goldTick'
                        ? 'bg-yellow-100 text-yellow-800'
                        : selectedPlanDetails.badgeType === 'silverTick'
                        ? 'bg-gray-100 text-gray-800'
                        : 'bg-blue-100 text-blue-800'
                    }`}>
                    {selectedPlanDetails.badgeType}
                  </span>
                </div>

                <div>
                  <h3 className="mb-2 text-sm font-medium text-gray-500">Status</h3>
                  <span
                    className={`inline-flex items-center px-3 py-1 text-sm rounded-full ${
                      selectedPlanDetails.isActive
                        ? 'bg-green-100 text-green-800'
                        : 'bg-red-100 text-red-800'
                    }`}>
                    {selectedPlanDetails.isActive ? 'Active' : 'Inactive'}
                  </span>
                </div>
              </div>

              <div>
                <h3 className="mb-2 text-sm font-medium text-gray-500">Created At</h3>
                <p className="text-sm">
                  {new Date(selectedPlanDetails.createdAt).toLocaleString()}
                </p>
              </div>

              <div>
                <h3 className="mb-2 text-sm font-medium text-gray-500">Updated At</h3>
                <p className="text-sm">
                  {new Date(selectedPlanDetails.updatedAt).toLocaleString()}
                </p>
              </div>

              <div>
                <h3 className="mb-2 text-sm font-medium text-gray-500">Description</h3>
                <div className="p-3 rounded bg-gray-50">
                  <p className="text-sm whitespace-pre-wrap">{selectedPlanDetails.description}</p>
                </div>
              </div>

              <div>
                <h3 className="mb-2 text-sm font-medium text-gray-500">Features</h3>
                <div className="p-3 rounded bg-gray-50">
                  {selectedPlanDetails.features && selectedPlanDetails.features.length > 0 ? (
                    <ul className="space-y-2">
                      {selectedPlanDetails.features.map((feature, index) => (
                        <li key={index} className="flex items-start text-sm">
                          <span className="mr-2">•</span>
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-sm text-gray-500">No features added</p>
                  )}
                </div>
              </div>
            </div>

            <div className="flex justify-end pt-6 mt-6 border-t border-gray-200">
              <button
                onClick={() => setIsDetailModalOpen(false)}
                className="px-4 py-2 text-black transition-colors border border-black rounded hover:bg-gray-50">
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SubscriptionPlan;
